/* Blogs management page in the shared admin page frame with filtering and sorting. */



import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Breadcrumb from '@/components/admin/Breadcrumb';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Plus, Pencil, Trash2, Eye, Star, Search, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  tldr?: string;
  image_url?: string;
  image_storage_path?: string;
  published: boolean;
  published_at: string;
  scheduled_at?: string;
  created_at: string;
  updated_at: string;
  views: number;
  is_featured: boolean;
}

type StatusFilter = 'all' | 'live' | 'scheduled' | 'concept';
type SortField = 'created_at' | 'views' | 'title';
type SortOrder = 'asc' | 'desc';

export default function BlogsPage() {
  const navigate = useNavigate();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<BlogPost[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [postToDelete, setPostToDelete] = useState<BlogPost | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

  useEffect(() => {
    fetchBlogs();
    checkAdminRole();
  }, []);

  useEffect(() => {
    applyFiltersAndSorting();
  }, [posts, searchQuery, statusFilter, sortField, sortOrder]);

  const checkAdminRole = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: adminData } = await supabase
        .from('admins')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (adminData?.role === 'superadmin') {
        setIsSuperAdmin(true);
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
    }
  };

  const fetchBlogs = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('blogs')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Fout bij het ophalen van blogs');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSorting = () => {
    let filtered = [...posts];

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        post =>
          post.title.toLowerCase().includes(query) ||
          post.excerpt.toLowerCase().includes(query)
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter(post => {
        const isLive = isCurrentlyPublished(post);
        const isScheduled = isPostScheduled(post);

        if (statusFilter === 'live') return isLive;
        if (statusFilter === 'scheduled') return isScheduled;
        if (statusFilter === 'concept') return !isLive && !isScheduled;
        return true;
      });
    }

    filtered.sort((a, b) => {
      let comparison = 0;

      if (sortField === 'title') {
        comparison = a.title.localeCompare(b.title);
      } else if (sortField === 'views') {
        comparison = a.views - b.views;
      } else {
        comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      }

      return sortOrder === 'asc' ? comparison : -comparison;
    });

    setFilteredPosts(filtered);
  };

  const handleDeletePost = async () => {
    if (!postToDelete) return;

    setIsLoading(true);
    try {
      const { error } = await supabase
        .from('blogs')
        .delete()
        .eq('id', postToDelete.id);

      if (error) throw error;

      toast.success('Blog post succesvol verwijderd');
      await fetchBlogs();
    } catch (error) {
      console.error('Error deleting blog:', error);
      toast.error('Fout bij het verwijderen van blog');
    } finally {
      setIsLoading(false);
      setIsDeleteDialogOpen(false);
      setPostToDelete(null);
    }
  };

  const isCurrentlyPublished = (post: BlogPost) => {
    return post.published_at && new Date(post.published_at) <= new Date();
  };

  const isPostScheduled = (post: BlogPost) => {
    if (post.scheduled_at && new Date(post.scheduled_at) > new Date()) {
      return true;
    }
    if (post.published_at && new Date(post.published_at) > new Date()) {
      return true;
    }
    return false;
  };

  const handleTogglePublished = async (post: BlogPost) => {
    try {
      const currentlyPublished = isCurrentlyPublished(post);

      const { error } = await supabase
        .from('blogs')
        .update({
          published: !currentlyPublished,
          published_at: !currentlyPublished ? new Date().toISOString() : null,
          scheduled_at: null
        })
        .eq('id', post.id);

      if (error) throw error;
      toast.success(currentlyPublished ? 'Blog offline gehaald' : 'Blog gepubliceerd');
      await fetchBlogs();
    } catch (error) {
      console.error('Error toggling published:', error);
      toast.error('Fout bij het wijzigen van publicatiestatus');
    }
  };

  const handleToggleFeatured = async (post: BlogPost) => {
    try {
      const { error } = await supabase
        .from('blogs')
        .update({ is_featured: !post.is_featured })
        .eq('id', post.id);

      if (error) throw error;
      toast.success(post.is_featured ? 'Featured verwijderd' : 'Blog featured gemaakt');
      await fetchBlogs();
    } catch (error) {
      console.error('Error toggling featured:', error);
      toast.error('Fout bij het wijzigen van featured status');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatScheduledDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('nl-NL', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }) + ' om ' + date.toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getBlogThumbnail = (post: BlogPost): string | null => {
    if (post.image_storage_path) {
      return supabase.storage.from('blog-images').getPublicUrl(post.image_storage_path).data.publicUrl;
    }
    return post.image_url || null;
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('nl-NL', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ArrowUpDown className="w-4 h-4 ml-1 inline opacity-30" />;
    }
    return sortOrder === 'asc'
      ? <ArrowUp className="w-4 h-4 ml-1 inline" />
      : <ArrowDown className="w-4 h-4 ml-1 inline" />;
  };

  return (
    <>
      <div className="max-w-7xl mx-auto px-6 py-12">
        <Breadcrumb pageName="Blogs" />

        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-[#1e2b22] mb-2">Blogs</h1>
            <p className="text-[#5a6552]">Beheer blog berichten en content</p>
          </div>
          <Button
            onClick={() => navigate('/kh-admin/blogs/editor')}
            className="bg-[#1e2b22] hover:bg-[#2a3a2f] text-white"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nieuwe blog
          </Button>
        </div>

        <div className="mb-6 flex flex-col gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Zoek op titel..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          <div className="flex flex-wrap gap-2">
            <Badge
              variant={statusFilter === 'all' ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                statusFilter === 'all'
                  ? 'bg-[#1e2b22] hover:bg-[#2a3a2f] text-white'
                  : 'hover:bg-[#e7e1d6]/40'
              }`}
              onClick={() => setStatusFilter('all')}
            >
              Alle ({posts.length})
            </Badge>
            <Badge
              variant={statusFilter === 'live' ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                statusFilter === 'live'
                  ? 'bg-[#5a6552] hover:bg-[#4a554a] text-white'
                  : 'hover:bg-[#e7e1d6]/40'
              }`}
              onClick={() => setStatusFilter('live')}
            >
              Live ({posts.filter(p => isCurrentlyPublished(p)).length})
            </Badge>
            <Badge
              variant={statusFilter === 'scheduled' ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                statusFilter === 'scheduled'
                  ? 'bg-[#7a6450] hover:bg-[#65533f] text-white'
                  : 'hover:bg-[#e7e1d6]/40'
              }`}
              onClick={() => setStatusFilter('scheduled')}
            >
              Gepland ({posts.filter(p => isPostScheduled(p)).length})
            </Badge>
            <Badge
              variant={statusFilter === 'concept' ? 'default' : 'outline'}
              className={`cursor-pointer transition-colors ${
                statusFilter === 'concept'
                  ? 'bg-[#cfcac2] hover:bg-[#bdb8ae] text-[#1e2b22]'
                  : 'hover:bg-[#e7e1d6]/40'
              }`}
              onClick={() => setStatusFilter('concept')}
            >
              Concept ({posts.filter(p => {
                const isLive = isCurrentlyPublished(p);
                const isScheduled = isPostScheduled(p);
                return !isLive && !isScheduled;
              }).length})
            </Badge>
          </div>
        </div>

        <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10"></TableHead>
                <TableHead
                  className="cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('title')}
                >
                  Titel{getSortIcon('title')}
                </TableHead>
                <TableHead className="hidden md:table-cell">Status</TableHead>
                <TableHead
                  className="hidden lg:table-cell cursor-pointer hover:bg-gray-50 select-none"
                  onClick={() => handleSort('created_at')}
                >
                  Datum{getSortIcon('created_at')}
                </TableHead>
                {isSuperAdmin && (
                  <TableHead
                    className="text-center cursor-pointer hover:bg-gray-50 select-none"
                    onClick={() => handleSort('views')}
                  >
                    <Eye className="w-4 h-4 inline" />
                    {getSortIcon('views')}
                  </TableHead>
                )}
                <TableHead className="text-center">Live</TableHead>
                <TableHead className="text-right"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPosts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isSuperAdmin ? 7 : 6} className="text-center py-12 text-gray-500">
                    {posts.length === 0 ? 'Geen blog posts gevonden' : 'Geen resultaten gevonden'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredPosts.map(post => {
                  const isLive = isCurrentlyPublished(post);
                  const isScheduled = isPostScheduled(post);

                  return (
                    <TableRow key={post.id}>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleToggleFeatured(post)}
                          className="p-1 h-auto"
                        >
                          <Star
                            className={`w-5 h-5 ${post.is_featured ? 'fill-[#c8a94a] text-[#c8a94a]' : 'text-[#cfcac2]'}`}
                          />
                        </Button>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            {getBlogThumbnail(post) ? (
                              <img
                                src={getBlogThumbnail(post)!}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center text-gray-400">
                                <Eye className="w-4 h-4" />
                              </div>
                            )}
                          </div>
                          <div className="space-y-1 min-w-0">
                            <div className="font-medium truncate">{post.title}</div>
                            <div className="text-sm text-gray-500 line-clamp-1 md:hidden">
                              {post.excerpt}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex flex-col gap-1 items-start">
                          {!isLive && !isScheduled && (
                            <Badge className="bg-gray-400 hover:bg-gray-400 text-white pointer-events-none">Concept</Badge>
                          )}
                          {isScheduled && (
                            <>
                              <Badge className="bg-[#7a6450] hover:bg-[#7a6450] text-white pointer-events-none">Gepland</Badge>
                              {(post.scheduled_at || post.published_at) && (
                                <span className="text-xs text-gray-500">
                                  {formatScheduledDateTime(post.scheduled_at || post.published_at!)}
                                </span>
                              )}
                            </>
                          )}
                          {isLive && (
                            <Badge className="bg-green-600 hover:bg-green-600 pointer-events-none">Live</Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-gray-600">
                        <div>{formatDate(post.created_at)}</div>
                        <div className="text-xs text-gray-400">{formatTime(post.created_at)}</div>
                      </TableCell>
                      {isSuperAdmin && (
                        <TableCell className="text-center font-medium">
                          {post.views.toLocaleString()}
                        </TableCell>
                      )}
                      <TableCell className="text-center">
                        <Switch
                          checked={isCurrentlyPublished(post)}
                          onCheckedChange={() => handleTogglePublished(post)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-2 justify-end">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => navigate(`/kh-admin/blogs/editor?id=${post.id}`)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => {
                              setPostToDelete(post);
                              setIsDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>

        <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Blog post verwijderen</DialogTitle>
              <DialogDescription>
                Weet je zeker dat je dit blog post wilt verwijderen? Deze actie kan niet ongedaan worden gemaakt.
              </DialogDescription>
            </DialogHeader>
            {postToDelete && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-semibold">{postToDelete.title}</p>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                Annuleren
              </Button>
              <Button variant="destructive" onClick={handleDeletePost}>
                Verwijderen
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </>
  );
}
